import React, { useState, useMemo } from 'react';
import {
  Container, Header, SpaceBetween, Box, Multiselect
} from '@cloudscape-design/components';

// ─── Shared tooltip style ────────────────────────────────────────────────────
const TOOLTIP_STYLE = {
  position: 'fixed',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 6,
  padding: '8px 12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  zIndex: 9999,
  pointerEvents: 'none',
};

// ─── Colour palette ──────────────────────────────────────────────────────────
const PALETTE = [
  '#1f77b4','#ff7f0e','#2ca02c','#d62728','#9467bd',
  '#8c564b','#e377c2','#7f7f7f','#bcbd22','#17becf',
  '#aec7e8','#ffbb78','#98df8a','#ff9896','#c5b0d5',
  '#c49c94','#f7b6d2','#c7c7c7','#dbdb8d','#9edae5',
];

// ─── SVG Pie Chart ────────────────────────────────────────────────────────────
export const PieChart = ({ slices, size = 200, formatCurrency }) => {
  const [tooltip, setTooltip] = useState(null);
  const cx = size / 2, cy = size / 2, r = size / 2 - 10;

  const total = slices.reduce((s, sl) => s + sl.value, 0);
  // P1: guard against zero total
  if (total === 0) return null;

  let cumAngle = -Math.PI / 2;
  const paths = slices.map((sl, i) => {
    // Clamp to avoid full-circle SVG arc bug
    const angle = Math.min((sl.value / total) * 2 * Math.PI, 2 * Math.PI - 0.001);
    const x1 = cx + r * Math.cos(cumAngle);
    const y1 = cy + r * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = cx + r * Math.cos(cumAngle);
    const y2 = cy + r * Math.sin(cumAngle);
    const large = angle > Math.PI ? 1 : 0;
    const midAngle = cumAngle - angle / 2;
    const lx = cx + (r * 0.65) * Math.cos(midAngle);
    const ly = cy + (r * 0.65) * Math.sin(midAngle);
    const pct = ((sl.value / total) * 100).toFixed(1);
    return { path: `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`, color: sl.color, label: sl.label, value: sl.value, pct, lx, ly, i };
  });

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <svg width={size} height={size} role="img" aria-label={`Modernization pie chart: ${paths.map(p => `${p.label} ${p.pct}%`).join(', ')}`}>
        {paths.map(p => (
          <path
            key={p.i}
            d={p.path}
            fill={p.color}
            stroke="#fff"
            strokeWidth={2}
            style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
            onMouseMove={e => setTooltip({ x: e.clientX, y: e.clientY, label: p.label, value: p.value, pct: p.pct })}
            onMouseLeave={() => setTooltip(null)}
          />
        ))}
        {paths.map(p => parseFloat(p.pct) > 5 && (
          <text key={`t${p.i}`} x={p.lx} y={p.ly} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize={11} fontWeight="bold" pointerEvents="none">
            {p.pct}%
          </text>
        ))}
      </svg>
      {tooltip && (
        <div style={{ ...TOOLTIP_STYLE, left: tooltip.x + 12, top: tooltip.y - 10, minWidth: 160 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{tooltip.label}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span>Annual Run Rate</span><span>{formatCurrency(tooltip.value)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span>Percentage</span><span>{tooltip.pct}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Stacked Horizontal Bar ───────────────────────────────────────────────────
const StackedBar = ({ label, segments, total, grandTotal, colorMap, onHover, onLeave, formatCurrency }) => {
  // Merge segments < 0.5% into an "Other" bucket so the bar always fills completely
  const rendered = [];
  const mergedIntoOther = [];
  let otherValue = 0;
  segments.forEach(seg => {
    const wPct = total > 0 ? (seg.value / total) * 100 : 0;
    if (wPct < 0.5) {
      otherValue += seg.value;
      mergedIntoOther.push(seg);
    } else {
      rendered.push(seg);
    }
  });
  if (otherValue > 0) {
    rendered.push({ key: '__other__', value: otherValue });
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, marginBottom: 4, color: '#555' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, maxWidth: 600, height: 24, display: 'flex', borderRadius: 4, overflow: 'hidden', background: '#eee' }}>
          {rendered.map((seg, i) => {
            const wPct = total > 0 ? (seg.value / total) * 100 : 0;
            const bg = seg.key === '__other__' ? '#ccc' : (colorMap[seg.key] || PALETTE[i % PALETTE.length]);
            // Pass only the relevant segments to the tooltip
            const hoverSegs = seg.key === '__other__' ? mergedIntoOther : [seg];
            return (
              <div
                key={seg.key}
                style={{ width: `${wPct}%`, height: '100%', background: bg, cursor: 'pointer' }}
                onMouseMove={e => onHover && onHover(e, label, hoverSegs)}
                onMouseLeave={onLeave}
              />
            );
          })}
        </div>
        <span style={{ fontSize: 12, color: '#333', whiteSpace: 'nowrap' }}>
          {formatCurrency(total)}
          {grandTotal > 0 && (
            <span style={{ marginLeft: 4 }}>
              ({((total / grandTotal) * 100).toFixed(1)}%)
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ModernizationCharts = ({ modernizationData, formatCurrency }) => {
  const [tooltip, setTooltip] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);

  const pathways = modernizationData?.pathways || [];
  const modernARR = modernizationData?.modernARR || 0;
  const totalARR = modernizationData?.totalARR || 0;

  // P1: memoize heavy derivations — only recompute when data or filter changes.
  // serviceCode is keyed case-insensitively so a service is not split into
  // multiple colors/segments when its code differs only by casing.
  const { uniqueServiceCodes, colorMap, serviceOptions, serviceNameByCode } = useMemo(() => {
    const allModern = pathways
      .filter(p => p.name !== 'Non Modern')
      .flatMap(p => p.services || []);
    // Canonical (lowercased) code -> display name (first occurrence wins).
    const serviceNameByCode = {};
    allModern.forEach(s => {
      const code = (s.serviceCode || '').toLowerCase();
      if (!(code in serviceNameByCode)) serviceNameByCode[code] = s.serviceName || s.serviceCode || code;
    });
    const codes = Object.keys(serviceNameByCode);
    const cMap = {};
    codes.forEach((code, i) => { cMap[code] = PALETTE[i % PALETTE.length]; });
    const opts = codes.map(code => ({ value: code, label: serviceNameByCode[code] }));
    return { uniqueServiceCodes: codes, colorMap: cMap, serviceOptions: opts, serviceNameByCode };
  }, [modernizationData]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilter = selectedServices.length > 0 ? selectedServices.map(s => s.value) : null;

  const { filteredPathways, filteredModernARR } = useMemo(() => {
    const fp = pathways
      .filter(p => p.name !== 'Non Modern')
      .map(p => ({
        ...p,
        services: activeFilter ? (p.services || []).filter(s => activeFilter.includes((s.serviceCode || '').toLowerCase())) : (p.services || []),
      }))
      .filter(p => p.services.length > 0);

    // Recompute modern ARR from filtered pathways so pie chart reflects the filter
    const fmARR = activeFilter
      ? fp.reduce((s, p) => s + p.services.reduce((ss, sv) => ss + sv.arr, 0), 0)
      : modernARR;

    return { filteredPathways: fp, filteredModernARR: fmARR };
  }, [modernizationData, selectedServices]); // eslint-disable-line react-hooks/exhaustive-deps

  // P0: When filter is active, "Other" = totalARR - filteredModernARR inflates the
  // "Other" slice. Instead show filteredNonModernARR as (unfiltered non-modern) +
  // (modern services not selected). Label the pie to clarify scope.
  const filteredNonModernARR = totalARR - filteredModernARR;
  const pieLabel = activeFilter ? 'Modernization Index (filtered)' : 'Modernization Index';

  return (
    <SpaceBetween size="l">

      {/* 1. Modernization Index Pie Chart */}
      <Container header={<Header variant="h3">{pieLabel}</Header>}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
          {totalARR > 0 ? (
            <PieChart
              size={220}
              formatCurrency={formatCurrency}
              slices={[
                { label: 'Modern Services', value: filteredModernARR, color: '#1f77b4' },
                { label: 'Other', value: filteredNonModernARR, color: '#e8c4c4' },
              ]}
            />
          ) : (
            <Box color="text-status-inactive">No ARR data available</Box>
          )}
          <SpaceBetween size="s">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 14, height: 14, background: '#1f77b4', borderRadius: 2 }} />
              <Box>Modern Services — {formatCurrency(filteredModernARR)} ({totalARR > 0 ? ((filteredModernARR / totalARR) * 100).toFixed(1) : 0}%)</Box>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 14, height: 14, background: '#e8c4c4', borderRadius: 2 }} />
              <Box>Other — {formatCurrency(filteredNonModernARR)} ({totalARR > 0 ? ((filteredNonModernARR / totalARR) * 100).toFixed(1) : 0}%)</Box>
            </div>
            <Box variant="strong">Modernization Index: {totalARR > 0 ? ((filteredModernARR / totalARR) * 100).toFixed(1) : 0}%</Box>
            {activeFilter && <Box color="text-status-inactive" fontSize="body-s">Showing selected services only</Box>}
          </SpaceBetween>
        </div>
      </Container>

      {/* 2 & 3. Pathway Stacked Bar + Service Filter */}
      <Container header={<Header variant="h3">Modernization by Pathway</Header>}>
        <div style={{ position: 'relative' }}>
          <SpaceBetween size="m">
            <div style={{ maxWidth: 320 }}>
              <Multiselect
                selectedOptions={selectedServices}
                onChange={({ detail }) => setSelectedServices(detail.selectedOptions)}
                options={serviceOptions}
                placeholder="Filter by service"
                selectedAriaLabel="Selected"
                deselectAriaLabel={e => `Remove ${e.label}`}
              />
            </div>

            {filteredPathways.length === 0 ? (
              <Box color="text-status-inactive">No modern services found</Box>
            ) : (
              <>
                {filteredPathways.map(pathway => {
                  // Aggregate by canonical (case-insensitive) code so a service
                  // with mixed-case codes renders as one segment, not several.
                  const segByCode = {};
                  (pathway.services || []).forEach(s => {
                    const code = (s.serviceCode || '').toLowerCase();
                    segByCode[code] = (segByCode[code] || 0) + s.arr;
                  });
                  const segments = Object.entries(segByCode).map(([key, value]) => ({ key, value }));
                  const pathTotal = segments.reduce((s, sg) => s + sg.value, 0);
                  return (
                    <StackedBar
                      key={pathway.name}
                      label={pathway.name}
                      segments={segments}
                      total={pathTotal}
                      grandTotal={totalARR}
                      colorMap={colorMap}
                      formatCurrency={formatCurrency}
                      onHover={(e, label, segs) => setTooltip({ x: e.clientX, y: e.clientY, label, segs })}
                      onLeave={() => setTooltip(null)}
                    />
                  );
                })}

                {/* Legend */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 8 }}>
                  {uniqueServiceCodes
                    .filter(code => !activeFilter || activeFilter.includes(code))
                    .map(code => (
                      <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 12, height: 12, background: colorMap[code], borderRadius: 2 }} />
                        <span style={{ fontSize: 11 }}>{serviceNameByCode[code] || code}</span>
                      </div>
                    ))}
                </div>

                <Box variant="strong" textAlign="right">
                  Total Modern Services Cost: {formatCurrency(filteredModernARR)}
                </Box>
              </>
            )}
          </SpaceBetween>

          {/* Tooltip — inside Container, outside SpaceBetween to avoid layout shift */}
          {tooltip && (
            <div style={{ ...TOOLTIP_STYLE, left: tooltip.x + 12, top: tooltip.y - 10, minWidth: 220 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{tooltip.label}</div>
              {tooltip.segs.map((seg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, background: colorMap[seg.key] || '#ccc', display: 'inline-block', borderRadius: 2 }} />
                    {serviceNameByCode[seg.key] || seg.key}
                  </span>
                  <span>{formatCurrency(seg.value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Container>

    </SpaceBetween>
  );
};

export default ModernizationCharts;
